import Scenes from "../enums/Scenes";
import Game from "./Game";
import WeaponType from "../enums/WeaponType";
import Router from "./Router";
import ApiService from "../../handlers/apiService";
import { LookInfo } from "../../handlers/types";

export default class SceneManager {
  Game: Phaser.Game;
  scene: Phaser.Scenes.ScenePlugin;
  MainScene: Phaser.Scene;
  UI_levelUpScene: Phaser.Scene;
  GameOverScene: Phaser.Scene;
  UI_treasureScene: Phaser.Scene;
  router: Router;

  constructor(game: Phaser.Game, scene: Phaser.Scenes.ScenePlugin) {
    this.Game = game;
    this.scene = scene;
    this.router = new Router(game);
  }

  Init() {
    this.MainScene = this.scene.get(Scenes.MainScene);
    this.UI_levelUpScene = this.scene.get(Scenes.UI_levelUpScene);
    this.GameOverScene = this.scene.get(Scenes.GameOverScene);
    this.router.init();
  }

  async NavigateToScene(
    scene: Scenes,
    params?: Record<string, any>,
    fromSettings: boolean = false
  ) {
    if (scene === Scenes.MainScene) {
      await Game.Core.StageService.init(Game.Core.PlayerOptions.SelectedStage)
        .then(() => {
          this.router.navigateToScene(scene, params);
        })
        .catch((error) => {
          console.error("Failed to initialize stage data:", error);
        });
      return;
    }

    // Clear stage data when navigating to main menu
    if (scene === Scenes.MainMenuScene) {
      Game.Core.StageService.clearStageData();
    }

    this.router.navigateToScene(scene, params, fromSettings);
  }

  PauseGame() {
    this.router.updateCurrentScene(Scenes.PauseScene);

    this.scene.pause(Scenes.MainScene);

    Game.Core.FreezeSnapshot(() => {
      this.scene.launch(Scenes.PauseScene);
    });
  }

  ResumeFromPause() {
    this.router.updateCurrentScene(Scenes.MainScene);

    this.scene.resume(Scenes.MainScene);
    this.scene.stop(Scenes.PauseScene);
  }

  EnterLevelUp() {
    this.router.updateCurrentScene(Scenes.UI_levelUpScene);

    this.scene.pause(this.MainScene);
    Game.Core.FreezeSnapshot(() => {
      this.scene.launch(this.UI_levelUpScene);
    });
  }

  ResumeFromLevelUp() {
    this.router.updateCurrentScene(Scenes.MainScene);

    this.scene.resume(this.MainScene);
    this.scene.stop(this.UI_levelUpScene);
  }

  EnterTreasure(coins: number, weapon?: WeaponType, look?: LookInfo) {
    this.router.updateCurrentScene(Scenes.UI_treasureScene);

    this.scene.pause(this.MainScene);
    Game.Core.FreezeSnapshot(() => {
      this.scene.launch(Scenes.UI_treasureScene, { coins, weapon, look });
    });
  }

  ResumeFromTreasure() {
    this.router.updateCurrentScene(Scenes.MainScene);

    this.scene.resume(this.MainScene);
    this.scene.stop(Scenes.UI_treasureScene);
  }

  async ShowGameOverScreen(
    coins: number,
    isLevelFinished: boolean = false,
    endgameReward: number = 0
  ) {
    try {
      // Get the current stage ID
      const currentStageId = Game.Core.PlayerOptions.SelectedStage;

      // Start GameOverScene with objectives
      this.router.updateCurrentScene(Scenes.GameOverScene);
      this.scene.pause(this.MainScene);

      // Fetch stages from API
      const objectives =
        await ApiService.getInstance().api.stagesObjectivesList(currentStageId);

      Game.Core.FreezeSnapshot(() => {
        this.scene.launch(this.GameOverScene, {
          coins: coins,
          objectives: objectives,
          isLevelFinished: isLevelFinished,
          endgameReward: endgameReward,
        });
      });
    } catch (error) {
      console.error("Failed to fetch objectives:", error);

      // Even in case of error, we'll still show the GameOver screen
      // with an empty objectives array
      this.router.updateCurrentScene(Scenes.GameOverScene);
      this.scene.pause(this.MainScene);

      Game.Core.FreezeSnapshot(() => {
        this.scene.launch(this.GameOverScene, {
          coins: coins,
          objectives: [],
          isLevelFinished: isLevelFinished,
          endgameReward: endgameReward,
        });
      });
    }
  }

  // "Repeat" button
  ResumeFromGameOver() {
    this.scene.stop(this.GameOverScene);
    this.NavigateToScene(Scenes.MainScene);
  }

  // "I'm done" button
  FinishGameFromGameOver() {
    this.scene.stop(Scenes.MainScene);
    this.scene.stop(Scenes.GameOverScene);
    this.NavigateToScene(Scenes.MainMenuScene);
  }
}
